import { create } from 'zustand';
import { ID, TaxonomyStoreState, FocusedItemState, AppAspect } from './types';

// Define the arguments for moveFocus
export interface MoveFocusArgs {
    direction: 'up' | 'down' | 'left' | 'right';
    // Pass all relevant data from TaxonomyExplorer needed to calculate next focus
    l1Items: AppAspect[];
    l2Items: AppAspect[];
    l3Items: AppAspect[];
}

// Define the actions for the store
interface TaxonomyStoreActions {
    setL1: (l1Id?: ID) => void;
    setL2: (l2Id?: ID) => void;
    setL3: (l3Id?: ID) => void;
    setTheme: (themeId?: ID) => void;
    // setSubTheme: (subThemeId?: ID) => void; // If needed later
    toggleAspectThemeList: (aspectId: ID) => void;
    clearAllExpandedThemes: () => void;
    resetPath: (level: 'l1' | 'l2' | 'l3' | 'theme') => void;
    clearThemeSelection: () => void;
    // Action to set the whole path, useful for breadcrumb clicks
    setPath: (path: Partial<TaxonomyStoreState['path']>) => void;
    // New actions for focus management
    setFocusedItem: (item: FocusedItemState | null) => void;
    moveFocus: (args: MoveFocusArgs) => void; // New action
}

// Create the Zustand store
export const useTaxonomyStore = create<TaxonomyStoreState & TaxonomyStoreActions>((set, get) => ({
    // Initial state
    path: {},
    expandedAspectThemeLists: new Set<ID>(),
    focusedItem: null, // Add focusedItem to the store state

    // Actions
    setPath: (newPath) => set(state => ({
        path: { ...state.path, ...newPath }
    })),

    setL1: (l1Id?: ID) => set(state => {
        const newPath = { l1: l1Id, l2: undefined, l3: undefined, theme: undefined, subTheme: undefined };
        let newFocusedItem = state.focusedItem;
        if (l1Id) {
            newFocusedItem = { level: 1, aspectName: l1Id };
        } else if (state.focusedItem?.level === 1 && state.focusedItem?.aspectName === state.path.l1) {
            // If deselecting the currently focused L1 item, clear focus
            newFocusedItem = null;
        }

        // Clear all expanded themes when navigating to a different L1 aspect
        const newExpandedAspectThemeLists = new Set<ID>();

        return {
            path: newPath,
            focusedItem: newFocusedItem,
            expandedAspectThemeLists: newExpandedAspectThemeLists
        };
    }),

    setL2: (l2Id?: ID) => set(state => {
        const newPath = { ...state.path, l2: l2Id, l3: undefined, theme: undefined, subTheme: undefined };
        let newFocusedItem = state.focusedItem;
        if (l2Id) {
            newFocusedItem = { level: 2, aspectName: l2Id };
        } else if (state.focusedItem?.level === 2 && state.focusedItem?.aspectName === state.path.l2) {
            // If deselecting focused L2, focus moves to parent L1
            newFocusedItem = state.path.l1 ? { level: 1, aspectName: state.path.l1 } : null;
        }

        // Clear all expanded themes when navigating to a different L2 aspect
        const newExpandedAspectThemeLists = new Set<ID>();

        return {
            path: newPath,
            focusedItem: newFocusedItem,
            expandedAspectThemeLists: newExpandedAspectThemeLists
        };
    }),

    setL3: (l3Id?: ID) => set(state => {
        const newPath = { ...state.path, l3: l3Id, theme: undefined, subTheme: undefined };
        let newFocusedItem = state.focusedItem;
        if (l3Id) {
            newFocusedItem = { level: 3, aspectName: l3Id };
        } else if (state.focusedItem?.level === 3 && state.focusedItem?.aspectName === state.path.l3) {
            // If deselecting focused L3, focus moves to parent L2
            newFocusedItem = state.path.l2 ? { level: 2, aspectName: state.path.l2 } : null;
        }

        // Clear all expanded themes when navigating to a different L3 aspect
        const newExpandedAspectThemeLists = new Set<ID>();

        return {
            path: newPath,
            focusedItem: newFocusedItem,
            expandedAspectThemeLists: newExpandedAspectThemeLists
        };
    }),

    setTheme: (themeId?: ID) => set(state => ({
        path: {
            ...state.path,
            theme: themeId,
            subTheme: undefined,
        }
    })),

    toggleAspectThemeList: (aspectId: ID) => set(state => {
        const newExpandedAspectThemeLists = new Set(state.expandedAspectThemeLists);
        if (newExpandedAspectThemeLists.has(aspectId)) {
            newExpandedAspectThemeLists.delete(aspectId);
        } else {
            newExpandedAspectThemeLists.add(aspectId);
        }
        return { expandedAspectThemeLists: newExpandedAspectThemeLists };
    }),

    clearAllExpandedThemes: () => set(state => ({
        expandedAspectThemeLists: new Set<ID>()
    })),

    resetPath: (level: 'l1' | 'l2' | 'l3' | 'theme') => set(state => {
        const currentPath = state.path;
        let newPath: TaxonomyStoreState['path'] = { ...currentPath };
        if (level === 'l1') {
            newPath = { l1: currentPath.l1 }; // Keep L1, clear L2, L3, Theme
        } else if (level === 'l2' && currentPath.l1) {
            newPath = { l1: currentPath.l1, l2: currentPath.l2 }; // Keep L1, L2, clear L3, Theme
        } else if (level === 'l3' && currentPath.l1 && currentPath.l2) {
            newPath = { l1: currentPath.l1, l2: currentPath.l2, l3: currentPath.l3 }; // Keep L1,L2,L3, clear Theme
        } else if (level === 'theme') {
            // This case might be handled by just setting theme to undefined
            // Or if a theme crumb is clicked, it might set the theme without clearing higher levels
            // For now, this action implies clearing deeper levels than the one specified.
            // Let's refine: this action clears *from* a certain level downwards
        }
        // This resetPath needs clearer definition based on breadcrumb interaction.
        // The current setL1, setL2, setL3 handle progressive clearing well.
        // Let's simplify: setPath is more flexible for breadcrumbs.
        return { path: newPath }; // This action might be redundant with setPath
    }),

    clearThemeSelection: () => set(state => ({
        path: { ...state.path, theme: undefined, subTheme: undefined }
    })),

    // New action for focus
    setFocusedItem: (item: FocusedItemState | null) => set({ focusedItem: item }),

    moveFocus: ({ direction, l1Items, l2Items, l3Items }: MoveFocusArgs) => {
        const { focusedItem, path, setFocusedItem, setL1, setL2, setL3 } = get();

        if (direction === 'up' || direction === 'down') {
            let currentList: AppAspect[] = [];
            let currentLevel: 1 | 2 | 3 | null = null;
            let targetLevelForNewFocus: 1 | 2 | 3 | null = null;

            if (!focusedItem) { // No focus, try to focus first item
                if (path.l3 && l3Items.length > 0) { targetLevelForNewFocus = 3; currentList = l3Items; }
                else if (path.l2 && l2Items.length > 0) { targetLevelForNewFocus = 2; currentList = l2Items; }
                else if (path.l1 && l1Items.length > 0) { targetLevelForNewFocus = 1; currentList = l1Items; }
                else if (l1Items.length > 0) { targetLevelForNewFocus = 1; currentList = l1Items; }

                if (targetLevelForNewFocus && currentList.length > 0) {
                    setFocusedItem({ level: targetLevelForNewFocus, aspectName: currentList[0].name });
                }
                return;
            }

            currentLevel = focusedItem.level;
            if (currentLevel === 1) currentList = l1Items;
            else if (currentLevel === 2) currentList = l2Items;
            else if (currentLevel === 3) currentList = l3Items;
            else return;

            if (currentList.length === 0) return;
            const currentIndex = currentList.findIndex(item => item.name === focusedItem.aspectName);

            if (currentIndex === -1) { // Focused item not in its supposed list, focus first
                setFocusedItem({ level: currentLevel, aspectName: currentList[0].name });
                return;
            }

            let newIndex = currentIndex;
            if (direction === 'up') newIndex = Math.max(0, currentIndex - 1);
            else if (direction === 'down') newIndex = Math.min(currentList.length - 1, currentIndex + 1);

            if (newIndex !== currentIndex) {
                setFocusedItem({ level: currentLevel, aspectName: currentList[newIndex].name });
            }
        } else if (direction === 'right') {
            if (focusedItem) {
                if (focusedItem.level === 1 && l1Items.some(i => i.name === focusedItem.aspectName)) {
                    // setL1 will update path and attempt to set focus to the L1 item itself.
                    // Further focus shift (e.g. to first L2 item) could be an effect in Explorer based on path change.
                    setL1(focusedItem.aspectName);
                } else if (focusedItem.level === 2 && l2Items.some(i => i.name === focusedItem.aspectName)) {
                    setL2(focusedItem.aspectName);
                }
                // No action for L3 focused item on ArrowRight for aspects
            }
        } else if (direction === 'left') {
            if (focusedItem) {
                if (focusedItem.level === 3) {
                    setL3(undefined); // Store action will set focus to L2 selected item
                } else if (focusedItem.level === 2) {
                    setL2(undefined); // Store action will set focus to L1 selected item
                } else if (focusedItem.level === 1) {
                    setL1(undefined); // Store action will clear focus
                }
            } else { // No item focused, act on deepest selected path
                if (path.l3 && path.l2) setL3(undefined); // Focus will go to L2 item
                else if (path.l2 && path.l1) setL2(undefined); // Focus will go to L1 item
                else if (path.l1) setL1(undefined); // Focus will be cleared
            }
        }
    },
})); 